import React, {useState, useEffect} from 'react';
import {OnSelectCallbackParam} from 'react-daterange-picker';
import Moment from 'moment';
import {extendMoment} from 'moment-range';
import wrap from 'wraptile';
import {curry} from 'ramda';

import {
    OnBlurEvent,
    OnAnchorElSet,
    OnMouseDivEvent,
    OnInputChangeEvent,
    OnStringSetState,
} from '@pluto/@types';
import humanizeRange from '@pluto/humanize-moment-range';
import {
    DateRangeState,
    DateRangePickerContainerProps,
    BlurComponentProps,
    HandlePickerSelectProps,
    DateRangePreselects,
    HandlePickerClearProps,
    HandlePickerEffect,
} from './DateRangeTypes';
import DateRangePicker from './DateRangePicker';
import {FORMAT} from './index';

const DAY_IN_SECONDS = 86399; // equivalent of 23:59:59

const moment = extendMoment(Moment as any);

export const handlePlaceholderClick = curry((setAnchorEl: OnAnchorElSet, event: OnMouseDivEvent) => {
    setAnchorEl(event.target as Element);
});

export const handleClose = wrap((setAnchorEl: OnAnchorElSet) => {
    setAnchorEl(null);
});

export const valueToMoment = (val: string | null): moment.Moment => moment(val, FORMAT);

export const getInitialRanges = (query: URLSearchParams, startRange: string, endRange: string): DateRangeState => {
    const beforeMomentStart = query.get(startRange);
    const beforeMomentEnd = query.get(endRange);
    
    if (beforeMomentStart && beforeMomentEnd)
        return moment.range(valueToMoment(beforeMomentStart), valueToMoment(beforeMomentEnd));
};

export const isToday = (ranges: OnSelectCallbackParam): boolean => {
    const today = moment().startOf('day').format(FORMAT);
    const start = ranges.start.format(FORMAT);
    const todayNight = moment().startOf('day').add(DAY_IN_SECONDS, 'seconds').format(FORMAT);
    const end = ranges.end.format(FORMAT);
    
    return today === start && todayNight === end;
};

export const isTomorrow = (ranges: OnSelectCallbackParam): boolean => {
    const today = moment().startOf('day').format(FORMAT);
    const start = ranges.start.format(FORMAT);
    const tomorrow = moment().startOf('day').add(1, 'day').add(DAY_IN_SECONDS, 'seconds').format(FORMAT);
    const end = ranges.end.format(FORMAT);
    
    return today === start && tomorrow === end;
};

export const isNextTwoWeeks = (ranges: OnSelectCallbackParam): boolean => {
    const today = moment().startOf('day').format(FORMAT);
    const start = ranges.start.format(FORMAT);
    const twoWeeks = moment().startOf('day').add(2, 'weeks').format(FORMAT);
    const end = ranges.end.format(FORMAT);
    
    return today === start && twoWeeks === end;
};

export const isLastTwoWeeks = (ranges: OnSelectCallbackParam | undefined): boolean => {
    if (!ranges)
        return false;
    
    const today = moment().startOf('day').format(FORMAT);
    const start = ranges.start.format(FORMAT);
    const lastTwoWeeks = moment().startOf('day').add(-2, 'weeks').format(FORMAT);
    const end = ranges.end.format(FORMAT);
    
    return today === end && lastTwoWeeks === start;
};

export const detectPreselect = (ranges: OnSelectCallbackParam): DateRangePreselects => {
    switch(true) {
    case isToday(ranges):
        return DateRangePreselects.Today;
    
    case isTomorrow(ranges):
        return DateRangePreselects.Tomorrow;
    
    case isNextTwoWeeks(ranges):
        return DateRangePreselects.NextTwoWeeks;
    
    case isLastTwoWeeks(ranges):
        return DateRangePreselects.LastTwoWeeks;
    
    default:
        return DateRangePreselects.CUSTOM;
    }
};

export const handlePickerSelect = curry((componentProps: HandlePickerSelectProps, selectedDates: OnSelectCallbackParam) => {
    const {
        onDatesSelect,
        setStartDate,
        setEndDate,
        setPreselect,
    } = componentProps;
    
    onDatesSelect(selectedDates);
    setStartDate(selectedDates.start.format(FORMAT));
    setEndDate(selectedDates.end.format(FORMAT));
    
    const newPreselect = detectPreselect(selectedDates);
    
    setPreselect(newPreselect);
});

export const handlePreselectChange = curry((componentProps: HandlePickerSelectProps, e: OnInputChangeEvent) => {
    const {
        onDatesSelect,
        setStartDate,
        setEndDate,
        setPreselect,
    } = componentProps;
    
    setPreselect(e.target.value as DateRangePreselects);
    
    const start = moment().startOf('day');
    const end = moment().startOf('day');
    switch(e.target.value) {
    case DateRangePreselects.Today:
        end.add(DAY_IN_SECONDS, 'second');
        break;
    
    case DateRangePreselects.Tomorrow:
        end.add(1, 'day');
        end.add(DAY_IN_SECONDS, 'second');
        break;
    
    case DateRangePreselects.NextTwoWeeks:
        end.add(2, 'weeks');
        break;
    
    case DateRangePreselects.LastTwoWeeks:
        start.add(-2, 'weeks');
        break;
    }
    
    const newDates = moment.range(start, end);
    
    setStartDate(newDates.start.format(FORMAT));
    setEndDate(newDates.end.format(FORMAT));
    onDatesSelect(newDates);
});

export const handleSimpleValueSet = curry((handleSet: OnStringSetState, e: OnInputChangeEvent) => {
    handleSet(e.target.value);
});

export const getDateStartRange = (value: string, newStart: Moment.Moment | undefined): OnSelectCallbackParam => {
    const start = newStart || moment().startOf('day');
    const end = moment(value, FORMAT);
    const newDates = moment.range(start, end);
    
    return newDates;
};

export const onDateEndBlur = curry((componentProps: BlurComponentProps, e: OnBlurEvent) => {
    const {
        dates,
        onDatesSelect,
        setPreselect,
    } = componentProps;
    
    const range = getDateStartRange(e.target.value, dates?.start);
    
    onDatesSelect(range);
    
    const newPreselect = detectPreselect(range);
    
    setPreselect(newPreselect);
});

export const getDateRange = (value: string, newEnd: Moment.Moment | undefined): OnSelectCallbackParam => {
    const end = newEnd || moment().startOf('day');
    const start = moment(value, FORMAT);
    const newDates = moment.range(start, end);
    
    return newDates;
};

export const onDateStartBlur = curry((componentProps: BlurComponentProps, e: OnBlurEvent) => {
    const {
        dates,
        onDatesSelect,
        setPreselect,
    } = componentProps;
    
    const range = getDateRange(e.target.value, dates?.end);
    
    onDatesSelect(range);
    
    const newPreselect = detectPreselect(range);
    
    setPreselect(newPreselect);
});

export const handleClear = wrap((setters: HandlePickerClearProps) => {
    const {
        onDatesSelect,
        setPreselect,
        setStartDate,
        setEndDate,
    } = setters;
    
    onDatesSelect(undefined);
    setPreselect(DateRangePreselects.CUSTOM);
    setStartDate('');
    setEndDate('');
});

export const getFullDate = (dates: [string, string]): string => {
    const [dateStart, dateEnd] = dates;
    
    if (dateStart && dateEnd)
        return `${dateStart} - ${dateEnd}`;
    
    return '';
};

export const handlePickerEffect = wrap((handlers: HandlePickerEffect) => {
    const {
        startDate,
        endDate,
        setPreselect,
    } = handlers;
    
    const dateStart = valueToMoment(startDate);
    const dateEnd = valueToMoment(endDate);
    const ranges = moment.range(dateStart, dateEnd);
    const preselect = detectPreselect(ranges);
    
    setPreselect(preselect);
});

const DateRangePickerContainer = (props: DateRangePickerContainerProps) => {
    const {
        onDatesSelect,
        placeholder,
        dates,
    } = props;
    const [startDate, setStartDate] = useState<string>(dates?.start?.format(FORMAT) || '');
    const [endDate, setEndDate] = useState<string>(dates?.end?.format(FORMAT) || '');
    const [anchorEl, setAnchorEl] = useState<Element | null>(null);
    const [preselect, setPreselect] = useState<DateRangePreselects>(DateRangePreselects.CUSTOM);
    const onEffect = handlePickerEffect({
        startDate,
        endDate,
        setPreselect,
    });
    
    useEffect(onEffect, [
        preselect,
        startDate,
        endDate,
    ]);
    
    const open = Boolean(anchorEl);
    const valueFull = getFullDate(humanizeRange(dates));
    
    const onPlaceholderClick = handlePlaceholderClick(setAnchorEl);
    const onClose = handleClose(setAnchorEl);
    
    const onPickerSelect = handlePickerSelect({
        onDatesSelect,
        setStartDate,
        setEndDate,
        setPreselect,
    });
    
    const onPreselectChange = handlePreselectChange({
        onDatesSelect,
        setStartDate,
        setEndDate,
        setPreselect,
    });
    
    const onStartDateChange = handleSimpleValueSet(setStartDate);
    
    const onStartDateBlur = onDateStartBlur({
        dates,
        onDatesSelect,
        setPreselect,
    });
    
    const onEndDateChange = handleSimpleValueSet(setEndDate);
    
    const onEndDateBlur = onDateEndBlur({
        dates,
        onDatesSelect,
        setPreselect,
    });
    
    const onClear = handleClear({
        onDatesSelect,
        setPreselect,
        setStartDate,
        setEndDate,
    });
    
    return (
        <DateRangePicker
            placeholder={placeholder}
            handlePlaceholderClick={onPlaceholderClick}
            valueFull={valueFull}
            open={open}
            anchorEl={anchorEl}
            handleClose={onClose}
            onPickerSelect={onPickerSelect}
            dates={dates}
            preselect={preselect}
            onPreselectChange={onPreselectChange}
            startDate={startDate}
            onStartDateChange={onStartDateChange}
            onStartDateBlur={onStartDateBlur}
            endDate={endDate}
            onEndDateChange={onEndDateChange}
            onEndDateBlur={onEndDateBlur}
            onClear={onClear}
        />
    );
};

export default DateRangePickerContainer;
